import React, { useRef } from 'react';
import PropTypes from 'prop-types';
import { isEmpty, merge } from 'lodash';
import generateTooltipDecoratorFunc from 'generate-tooltip-decorator';
import generateYAxisDecoratorFunc from 'generate-y-axis-decorator';
import applyColumnFormatFunc from 'apply-column-format';
import Highcharts from 'highcharts';
import HighchartsMore from 'highcharts/highcharts-more';
import HighchartsReact from 'highcharts-react-official';
import HighchartsExtraLabelFormat from 'highcharts-extra-label-format';
import { chartContextButtonInit, contextButtonVisualSettings } from './chartSettings';

require('highcharts-exporting')(Highcharts);
require('highcharts-export-csv')(Highcharts);
require('highcharts/modules/accessibility')(Highcharts);
require('highcharts-no-data-to-display')(Highcharts);

HighchartsMore(Highcharts);
HighchartsExtraLabelFormat(Highcharts);
chartContextButtonInit();

export default function PmChart({ showCSVDownload = true, height = 200, config }) {
  const highcharts = useRef(null);
  let isOldStructure = true;
  let currentConfig = JSON.parse(config);

  if (currentConfig.highChartSettings) {
    isOldStructure = false;
    currentConfig = currentConfig.highChartSettings;
  }

  currentConfig = merge(currentConfig, {
    chart: { height },
    title: { text: '' },
    legend: showCSVDownload && currentConfig.legend ? currentConfig.legend : false,
    credits: false,
    navigation: {
      ...(currentConfig.legend
        ? {
            buttonOptions: {
              enabled: showCSVDownload,
              align: 'right',
              x: -50,
            },
          }
        : {}),
    },
    exporting: {
      ...(currentConfig.exporting
        ? {
            enabled: showCSVDownload || false,
            csv: {
              columnHeaderFormatter: null,
              dateFormat: '%Y-%m-%d %H:%M:%S',
              decimalPoint: null,
              itemDelimiter: null,
              lineDelimiter: '\n',
            },
            buttons: {
              contextButton: {
                menuItems: [
                  {
                    textKey: 'downloadCSV',
                    onclick: () => highcharts.current.chart.downloadCSV(),
                  },
                ],
                ...contextButtonVisualSettings,
              },
            },
          }
        : { enabled: false }),
    },
    lang: {
      ...(currentConfig.exporting
        ? {
            contextButtonTitle: 'Download CSV',
            accessibility: {
              chartContainerLabel: currentConfig.exporting?.filename || 'Chart',
              userTitle: currentConfig.exporting?.filename || 'Chart',
              autoAccessible: false,
              description: currentConfig.exporting?.filename || 'Chart',
            },
          }
        : {}),
    },
    noData: {
      style: {
        fontWeight: 'bold',
        fontSize: '20px',
        color: '#404040',
      },
    },
  });

  const chartType = currentConfig.chart.type;

  if (isOldStructure) {
    if (chartType === 'pie') {
      const tooltipFormatter = generateTooltipDecoratorFunc({
        shared: false,
        chartType: 'pie',
      });

      currentConfig.tooltip = { formatter: tooltipFormatter };
    } else {
      const tooltipFormatter = generateTooltipDecoratorFunc({
        shared: false,
        chartType: 'column',
      });
      const yAxisFormatter = generateYAxisDecoratorFunc(applyColumnFormatFunc);

      currentConfig.tooltip = { formatter: tooltipFormatter };

      let newYAxis = [];

      if (currentConfig.yAxis && currentConfig.yAxis.length > 0) {
        newYAxis = currentConfig.yAxis.map((axis) => ({
          ...axis,
          labels: { formatter: yAxisFormatter },
        }));
      }

      currentConfig.yAxis = newYAxis;
    }
  }

  return <HighchartsReact highcharts={Highcharts} options={currentConfig} ref={highcharts} />;
}

PmChart.propTypes = {
  config: PropTypes.string.isRequired,
  showCSVDownload: PropTypes.bool,
  height: PropTypes.number,
};
